import React from "react";

import {MinusIcon, PlusIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {match} from "ts-pattern";

import * as workouts from "@client/features/workouts";

import {SegTime} from "@client/forms/segtime/SegTime";
import {formatSeconds} from "@client/forms/segtime/machine";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import {useDebounce} from "@client/hooks";
import * as forms from "@client/site/forms";
import {CardioMetric, classNames} from "@reactivated";

import {
    CARD,
    CARDIO_DISTANCE_UNITS,
    DISTANCE_LABELS,
    type DistanceType,
    type Measure,
    type SetOutput,
    type SetType,
    type SpeedType,
    computePaceSeconds,
    defaultDistanceType,
    deriveMeasure,
    makeCardioSet,
    paceUnit,
    speedLabel,
    speedTypeFor,
} from "./model";

// Big, chrome-less value styling shared by every reading cell so SegTime and the
// distance input line up.
const BARE_VALUE = "text-[17px] font-medium";
const BARE_INPUT = classNames(
    BARE_VALUE,
    "w-full min-w-0 border-0 bg-transparent p-0 text-gray-900 outline-none",
    "placeholder:text-gray-300 focus:ring-0",
);

// A small labeled bubble that sits under a segment's reading. Editable
// "settings" (the things you dial on the machine) read in indigo; the read-only
// derived pace reads in neutral stone so it never looks like an input.
function ChipFrame(props: {
    tone: "setting" | "derived";
    label: string;
    children: React.ReactNode;
}) {
    const setting = props.tone === "setting";

    return (
        <div
            className={classNames(
                "flex min-w-0 flex-1 basis-0 flex-col rounded-lg px-2.5 py-1.5",
                setting ? "bg-indigo-50" : "bg-stone-100",
            )}
        >
            <div className="flex items-center gap-1">
                <span
                    className={classNames(
                        "text-[9px] font-semibold tracking-wide uppercase",
                        setting ? "text-indigo-600" : "text-gray-500",
                    )}
                >
                    {props.label}
                </span>
            </div>
            {props.children}
        </div>
    );
}

// An editable equipment setting (speed / incline / level). Speed and incline are
// decimals; resistance is a unit-less integer level.
function SettingChip(props: {
    name: string;
    label: string;
    value: string;
    suffix?: string;
    integerOnly?: boolean;
    readOnly: boolean;
    onChange: (value: string | null) => void;
}) {
    return (
        <ChipFrame tone="setting" label={props.label}>
            <div className="flex items-baseline gap-1">
                <label htmlFor={props.name} className="sr-only">
                    {props.label}
                </label>
                <widgets.DecimalInput
                    name={props.name}
                    value={props.value}
                    placeholder="—"
                    integerOnly={props.integerOnly}
                    readOnly={props.readOnly}
                    onChange={props.onChange}
                    className="
                        w-full min-w-0 border-0 bg-transparent p-0 text-base
                        font-semibold text-gray-900 outline-none
                        placeholder:text-gray-300
                        focus:ring-0
                    "
                />
                {props.suffix != null && (
                    <span className="text-[11px] font-medium text-indigo-600">
                        {props.suffix}
                    </span>
                )}
            </div>
        </ChipFrame>
    );
}

// The incline / resistance chip driven by an exercise type's cardio_metrics.
function MetricChip(props: {
    metric: CardioMetric;
    set: Pick<SetOutput, "uuid" | "incline" | "resistance">;
    readOnly: boolean;
    onChange: (patch: Partial<SetOutput>) => void;
}) {
    const isResistance = props.metric === "RESISTANCE";

    return (
        <SettingChip
            name={`${props.set.uuid}-${isResistance ? "resistance" : "incline"}`}
            label={isResistance ? "Level" : "Incline"}
            suffix={isResistance ? undefined : "%"}
            integerOnly={isResistance}
            value={
                isResistance
                    ? (props.set.resistance?.toString() ?? "")
                    : (props.set.incline ?? "")
            }
            readOnly={props.readOnly}
            onChange={(next) =>
                props.onChange(
                    isResistance
                        ? {resistance: next == null ? null : parseInt(next, 10)}
                        : {incline: next},
                )
            }
        />
    );
}

// The implied average pace, read-only and never stored — it is recomputed from
// the reading each render. Until time and distance are both present there is
// nothing to derive, so it reads as an em dash, the same placeholder the
// editable chips show when empty.
function PaceChip(props: {paceSeconds: SecondsValue | null; unit: string}) {
    return (
        <ChipFrame tone="derived" label="Avg pace">
            <div className="text-base font-semibold text-gray-600">
                {props.paceSeconds == null ? (
                    <span className="text-gray-300">—</span>
                ) : (
                    formatSeconds(props.paceSeconds)
                )}
                <span className="ml-1 text-[11px] font-medium text-gray-400">
                    {props.unit}
                </span>
            </div>
        </ChipFrame>
    );
}

// The row of chips under a reading: Speed (always), the equipment metrics, and —
// for steady, where a row carries both time and distance — the derived pace.
// Shared by both the steady reading and each interval segment.
//
// `pace` decides whether the row has a pace chip at all, which is a property of
// the row and not of what has been typed into it: steady rows always pass one
// (with a null `seconds` until the reading is complete) so the chips keep their
// widths as a set is filled in, and interval segments pass null because pace is
// meaningless on a single-measure segment.
function SettingChips(props: {
    nameKey: string;
    set: SetOutput | null;
    speedType: SpeedType;
    cardioMetrics: CardioMetric[];
    readOnly: boolean;
    onPatch: (patch: Partial<SetOutput>) => void;
    pace: {seconds: SecondsValue | null} | null;
}) {
    return (
        <div className="flex gap-2">
            <SettingChip
                name={`${props.nameKey}-speed`}
                label="Speed"
                suffix={speedLabel(props.speedType)}
                value={props.set?.speed ?? ""}
                readOnly={props.readOnly}
                onChange={(value) =>
                    props.onPatch({speed: value, speed_type: props.speedType})
                }
            />
            {props.cardioMetrics.map(
                (metric) =>
                    props.set != null && (
                        <MetricChip
                            key={metric}
                            metric={metric}
                            set={props.set}
                            readOnly={props.readOnly}
                            onChange={props.onPatch}
                        />
                    ),
            )}
            {props.pace != null && (
                <PaceChip
                    paceSeconds={props.pace.seconds}
                    unit={paceUnit(props.speedType)}
                />
            )}
        </div>
    );
}

const Chevron = () => (
    <svg
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
        className="size-3.5 shrink-0 text-gray-400"
    >
        <path
            d="M6 8l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// The bordered, connected container the cells live inside.
function ConnectedGroup(props: {children: React.ReactNode}) {
    return (
        <div
            className="
                flex items-stretch overflow-hidden rounded-lg bg-gray-50 ring-1
                ring-gray-200 ring-inset
            "
        >
            {props.children}
        </div>
    );
}

const CellDivider = () => <div aria-hidden="true" className="w-px bg-gray-200" />;

function ValueCell(props: {eyebrow: string; children: React.ReactNode}) {
    return (
        <div className="min-w-0 flex-1 px-3 py-2">
            <div className="text-[10px] tracking-wide text-gray-400 uppercase">
                {props.eyebrow}
            </div>
            <div className="mt-0.5">{props.children}</div>
        </div>
    );
}

// A cell that opens a picker. The visible value sits under the eyebrow with a
// chevron; an invisible native <select> is layered on top so the control stays
// fully accessible and behaves natively on mobile.
function CellPicker(props: {
    eyebrow: string;
    display: string;
    current: string;
    options: Array<[string, string]>;
    width: string;
    readOnly: boolean;
    onChange: (value: string) => void;
}) {
    return (
        <div className={classNames("relative shrink-0 px-3 py-2", props.width)}>
            <div className="text-[10px] tracking-wide text-gray-400 uppercase">
                {props.eyebrow}
            </div>
            <div className="mt-0.5 flex items-center gap-1">
                <span className="text-[17px] font-medium text-gray-900">
                    {props.display}
                </span>
                <Chevron />
            </div>
            <select
                aria-label={props.eyebrow}
                value={props.current}
                disabled={props.readOnly}
                onChange={(event) => props.onChange(event.target.value)}
                className="
                    absolute inset-0 size-full cursor-pointer opacity-0
                    disabled:cursor-default
                "
            >
                {props.options.map(([value, label]) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
        </div>
    );
}

// Steady cardio: Duration · Distance · Unit, all visible at once.
function SteadyFields(props: {
    set: SetOutput;
    readOnly: boolean;
    distanceType: DistanceType;
    onDuration: (value: SecondsValue | null) => void;
    onDistance: (value: string | null) => void;
    onUnit: (distanceType: DistanceType) => void;
}) {
    const {set, readOnly} = props;

    return (
        <ConnectedGroup>
            <ValueCell eyebrow="Duration">
                <SegTime
                    bare
                    hours
                    name={`${set.uuid}-duration`}
                    label="Duration"
                    value={set.duration?.toString() ?? null}
                    readOnly={readOnly}
                    onChange={(value) =>
                        props.onDuration(value == null ? null : parseInt(value, 10))
                    }
                    className={BARE_VALUE}
                />
            </ValueCell>
            <CellDivider />
            <ValueCell eyebrow="Distance">
                <widgets.DecimalInput
                    name={`${set.uuid}-distance`}
                    ariaLabel="Distance"
                    value={set.distance ?? ""}
                    placeholder="0.00"
                    readOnly={readOnly}
                    onChange={props.onDistance}
                    className={BARE_INPUT}
                />
            </ValueCell>
            <CellDivider />
            <CellPicker
                eyebrow="Unit"
                display={DISTANCE_LABELS[props.distanceType]}
                current={props.distanceType}
                options={CARDIO_DISTANCE_UNITS.map((unit) => [
                    unit,
                    DISTANCE_LABELS[unit],
                ])}
                width="w-16"
                readOnly={readOnly}
                onChange={(value) => props.onUnit(value as DistanceType)}
            />
        </ConnectedGroup>
    );
}

// An interval segment: one value, measured by Time or by a distance unit picked
// from the same dropdown.
function IntervalFields(props: {
    set: SetOutput | null;
    nameKey: string;
    readOnly: boolean;
    measure: Measure;
    distanceType: DistanceType;
    onDuration: (value: SecondsValue | null) => void;
    onDistance: (value: string | null) => void;
    onMeasure: (measure: Measure, distanceType?: DistanceType) => void;
}) {
    const measureOptions: Array<[string, string]> = [
        ["TIME", "Time"],
        ...CARDIO_DISTANCE_UNITS.map(
            (unit) => [unit, DISTANCE_LABELS[unit]] as [string, string],
        ),
    ];

    return (
        <ConnectedGroup>
            <ValueCell eyebrow={props.measure === "TIME" ? "Duration" : "Distance"}>
                {props.measure === "TIME" ? (
                    <SegTime
                        bare
                        name={`${props.nameKey}-duration`}
                        label="Duration"
                        value={props.set?.duration?.toString() ?? null}
                        readOnly={props.readOnly}
                        onChange={(value) =>
                            props.onDuration(value == null ? null : parseInt(value, 10))
                        }
                        className={BARE_VALUE}
                    />
                ) : (
                    <widgets.DecimalInput
                        name={`${props.nameKey}-distance`}
                        ariaLabel="Distance"
                        value={props.set?.distance ?? ""}
                        placeholder="0"
                        readOnly={props.readOnly}
                        onChange={props.onDistance}
                        className={BARE_INPUT}
                    />
                )}
            </ValueCell>
            <CellDivider />
            <CellPicker
                eyebrow="Measure"
                display={
                    props.measure === "TIME"
                        ? "Time"
                        : DISTANCE_LABELS[props.distanceType]
                }
                current={props.measure === "TIME" ? "TIME" : props.distanceType}
                options={measureOptions}
                width="w-24"
                readOnly={props.readOnly}
                onChange={(value) =>
                    value === "TIME"
                        ? props.onMeasure("TIME")
                        : props.onMeasure("DISTANCE", value as DistanceType)
                }
            />
        </ConnectedGroup>
    );
}

// Steady cardio for a single set: distance, duration and speed are independent
// readings, alongside a read-only chip for the pace they imply; that readout is
// never stored.
function CardioSteady(props: {
    handler: workouts.ExerciseSet;
    readOnly: boolean;
    cardioMetrics: CardioMetric[];
}) {
    const {handler, readOnly} = props;
    const isImperial = forms.useIsImperial();

    const distanceType = handler.distance_type ?? defaultDistanceType(isImperial);
    const speedType = handler.speed_type ?? speedTypeFor(distanceType);

    const paceSeconds = computePaceSeconds({
        durationSeconds: handler.duration,
        distance: handler.distance,
        distanceType,
        speedType,
    });

    return (
        <div className={classNames(CARD, "flex flex-col gap-2 p-4")}>
            <SteadyFields
                set={handler}
                readOnly={readOnly}
                distanceType={distanceType}
                onDuration={(value) => handler.__update({duration: value})}
                onDistance={(value) =>
                    handler.__update({distance: value, distance_type: distanceType})
                }
                onUnit={(next) =>
                    handler.__update({
                        distance_type: next,
                        speed_type: speedTypeFor(next),
                    })
                }
            />
            <SettingChips
                nameKey={handler.uuid}
                set={handler}
                speedType={speedType}
                cardioMetrics={props.cardioMetrics}
                readOnly={readOnly}
                onPatch={(patch) => handler.__update(patch)}
                pace={{seconds: paceSeconds}}
            />
        </div>
    );
}

// Steady cardio: one continuous reading per set, no list chrome.
function SteadyCardioSets(props: {
    exercise: workouts.CardioExercise;
    readOnly: boolean;
}) {
    return (
        <div className="flex flex-col gap-3 py-1">
            {props.exercise.sets.map((exerciseSet) => (
                <CardioSteady
                    key={exerciseSet.uuid}
                    handler={exerciseSet}
                    readOnly={props.readOnly}
                    cardioMetrics={props.exercise.type.casted_cardio_metrics}
                />
            ))}
        </div>
    );
}

// Rounds tick instantly on the screen, but materializing/deleting the underlying
// sets waits until the user stops stepping — long enough to absorb a held button,
// short enough to feel committed.
const STEP_DEBOUNCE_MS = 400;

const stepperButton = classNames(
    "flex size-6 items-center justify-center rounded-md bg-white",
    "text-gray-700 ring-1 ring-gray-300 ring-inset disabled:opacity-40",
);

const SegmentDivider = () => (
    <div aria-hidden="true" className="my-3 h-px bg-gray-200" />
);

// The small filled-circle remove control on warmup / cooldown, matching the
// exercise Options button at a smaller size.
function RemoveButton(props: {label: string; readOnly: boolean; onClick: () => void}) {
    return (
        <button
            type="button"
            aria-label={props.label}
            disabled={props.readOnly}
            onClick={() => {
                hardware.haptic("Medium");
                props.onClick();
            }}
            className="
                flex size-6 items-center justify-center rounded-full bg-stone-200/70
                text-gray-400
                hover:text-gray-600
                focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                focus:ring-offset-gray-100 focus:outline-hidden
                disabled:opacity-50
            "
        >
            <XMarkIcon className="size-4 text-gray-600" aria-hidden="true" />
        </button>
    );
}

function Stepper(props: {
    value: number;
    readOnly: boolean;
    onChange: (value: number) => void;
}) {
    return (
        <div className="flex items-center gap-2">
            <button
                type="button"
                aria-label="Remove round"
                disabled={props.readOnly || props.value <= 1}
                onClick={() => {
                    hardware.haptic("Light");
                    props.onChange(props.value - 1);
                }}
                className={stepperButton}
            >
                <MinusIcon className="size-4" aria-hidden="true" />
            </button>
            <span
                aria-label="Rounds"
                className="min-w-5 text-center text-base font-semibold text-gray-900"
            >
                {props.value}
            </span>
            <button
                type="button"
                aria-label="Add round"
                disabled={props.readOnly}
                onClick={() => {
                    hardware.haptic("Light");
                    props.onChange(props.value + 1);
                }}
                className={stepperButton}
            >
                <PlusIcon className="size-4" aria-hidden="true" />
            </button>
            <span className="text-xs text-gray-500">rounds</span>
        </div>
    );
}

function AddRow(props: {label: string; readOnly: boolean; onClick: () => void}) {
    return (
        <button
            type="button"
            disabled={props.readOnly}
            onClick={() => {
                hardware.haptic("Light");
                props.onClick();
            }}
            className="
                flex w-full items-center justify-center gap-1.5 rounded-xl border
                border-dashed border-gray-300 py-3 text-sm font-medium text-gray-500
                disabled:opacity-50
            "
        >
            + {props.label}
        </button>
    );
}

// A single labeled segment: its role heading, the Time/Distance reading, and the
// equipment-setting chips below.
function Segment(props: {
    role?: string;
    tone?: "work" | "rest";
    set: SetOutput | null;
    nameKey: string;
    measure: Measure;
    distanceType: DistanceType;
    cardioMetrics: CardioMetric[];
    readOnly: boolean;
    onDuration: (value: SecondsValue | null) => void;
    onDistance: (value: string | null) => void;
    onMeasure: (measure: Measure, distanceType?: DistanceType) => void;
    onPatch: (patch: Partial<SetOutput>) => void;
    // Rendered at the right of the role heading: the rounds stepper on "On", the
    // remove button on warmup / cooldown.
    trailing?: React.ReactNode;
}) {
    const speedType = speedTypeFor(props.distanceType);

    return (
        <div className="flex flex-col gap-2">
            {props.role != null && (
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        {props.tone != null && (
                            <span
                                className={classNames(
                                    "size-1.5 rounded-full",
                                    props.tone === "rest"
                                        ? "bg-amber-500"
                                        : "bg-indigo-600",
                                )}
                            />
                        )}
                        <span className="text-sm font-semibold text-gray-900">
                            {props.role}
                        </span>
                    </div>
                    {props.trailing}
                </div>
            )}
            <IntervalFields
                set={props.set}
                nameKey={props.nameKey}
                readOnly={props.readOnly}
                measure={props.measure}
                distanceType={props.distanceType}
                onDuration={props.onDuration}
                onDistance={props.onDistance}
                onMeasure={props.onMeasure}
            />
            <SettingChips
                nameKey={props.nameKey}
                set={props.set}
                speedType={speedType}
                cardioMetrics={props.cardioMetrics}
                readOnly={props.readOnly}
                onPatch={props.onPatch}
                pace={null}
            />
        </div>
    );
}

function CardioIntervals(props: {
    exercise: workouts.CardioExercise;
    readOnly: boolean;
    isTemplate: boolean;
    onSetsRemoved: (uuids: string[]) => void;
}) {
    const {exercise} = props;
    const isImperial = forms.useIsImperial();

    const warmup = exercise.sets.find((set) => set.type === "WARM_UP") ?? null;
    const cooldown = exercise.sets.find((set) => set.type === "COOL_DOWN") ?? null;
    const workSets = exercise.sets.filter((set) => set.type === "WORKING");
    const restSets = exercise.sets.filter((set) => set.type === "REST");
    // `.at(0)` (not `[0]`) so the type is genuinely nullable: a fresh interval
    // block always seeds a work/rest pair, but an empty array still yields null at
    // runtime — the commit/reconcile below guards on it.
    const work = workSets.at(0) ?? null;
    const rest = restSets.at(0) ?? null;
    const rounds = Math.max(workSets.length, restSets.length);

    const cardioMetrics: CardioMetric[] = exercise.type.casted_cardio_metrics;

    // Measure is per role (a 400m On above a 1:30 Off). Rounds stay uniform —
    // every WORKING shares one measure, every REST shares one — so the choice
    // lives in component state keyed by role, seeded from whatever was stored.
    const [measures, setMeasures] = React.useState<Record<string, Measure>>({
        WARM_UP: deriveMeasure(warmup),
        WORKING: deriveMeasure(work),
        REST: deriveMeasure(rest),
        COOL_DOWN: deriveMeasure(cooldown),
    });

    // The stepper drives this transient count for instant feedback; the real
    // sets only reconcile to it once stepping settles. Re-seed only when a
    // different exercise mounts into this slot — keying on the live `rounds`
    // would fire on our own commits and snap an in-progress tap backward.
    const [pendingRounds, setPendingRounds] = React.useState(rounds);
    React.useEffect(() => {
        // Re-seed both the rounds and per-role measures on exercise swap only,
        // never on the count/measure we ourselves commit.
        setPendingRounds(rounds);
        setMeasures({
            WARM_UP: deriveMeasure(warmup),
            WORKING: deriveMeasure(work),
            REST: deriveMeasure(rest),
            COOL_DOWN: deriveMeasure(cooldown),
        });
    }, [exercise.uuid]);

    const distanceTypeFor = (set: SetOutput | null): DistanceType =>
        set?.distance_type ?? (isImperial ? "MILES" : "KILOMETERS");

    const updateRole = (role: SetType, patch: Partial<SetOutput>) => {
        exercise.__update({
            sets: exercise.sets.map((set) =>
                set.type === role ? {...set, ...patch} : set,
            ),
        });
    };

    const setRoleMeasure = (
        role: SetType,
        measure: Measure,
        distanceType?: DistanceType,
    ) => {
        setMeasures((previous) => ({...previous, [role]: measure}));
        exercise.__update({
            sets: exercise.sets.map((set) =>
                set.type === role
                    ? {
                          ...set,
                          duration: measure === "TIME" ? set.duration : null,
                          distance: measure === "DISTANCE" ? set.distance : null,
                          ...(distanceType != null
                              ? {
                                    distance_type: distanceType,
                                    speed_type: speedTypeFor(distanceType),
                                }
                              : {}),
                      }
                    : set,
            ),
        });
    };

    // Reconcile the materialized sets to the count the user landed on. Computed
    // once against committed reality, so an overshoot (4 → 6 → 4) nets to nothing
    // and intermediate adds are never persisted — nothing stale to delete.
    const commitRounds = (target: number) => {
        if (target === rounds || work == null || rest == null) {
            return;
        }

        if (target > rounds) {
            // A fresh interval block always seeds a work/rest pair, so the first
            // of each role is the template every added round is cloned from. The
            // clone inherits the source set's status (complete live, incomplete
            // in a template).
            const added = Array.from({length: target - rounds}).flatMap(() => [
                {...work, uuid: crypto.randomUUID()},
                {...rest, uuid: crypto.randomUUID()},
            ]);

            exercise.__update({
                sets: [
                    ...exercise.sets.filter((set) => set.type !== "COOL_DOWN"),
                    ...added,
                    ...exercise.sets.filter((set) => set.type === "COOL_DOWN"),
                ],
            });
        } else {
            const removed = new Set([
                ...workSets.slice(target).map((set) => set.uuid),
                ...restSets.slice(target).map((set) => set.uuid),
            ]);

            // Delete the trimmed rows first, then mutate local state so the
            // workout's own debounced update_workout persists the survivors after
            // the deletes have already gone out.
            props.onSetsRemoved([...removed]);
            exercise.__update({
                sets: exercise.sets.filter((set) => removed.has(set.uuid) === false),
            });
        }
    };

    useDebounce(() => commitRounds(pendingRounds), STEP_DEBOUNCE_MS, [pendingRounds]);

    const addBoundary = (type: "WARM_UP" | "COOL_DOWN") => {
        const created = makeCardioSet(isImperial, type, 300, props.isTemplate);
        setMeasures((previous) => ({...previous, [type]: "TIME"}));
        exercise.__update({
            sets:
                type === "WARM_UP"
                    ? [created, ...exercise.sets]
                    : [...exercise.sets, created],
        });
    };

    const removeBoundary = (uuid: string) => {
        exercise.__update({
            sets: exercise.sets.filter((set) => set.uuid !== uuid),
        });
        props.onSetsRemoved([uuid]);
    };

    const segmentProps = (role: SetType, set: SetOutput | null, nameKey: string) => ({
        set,
        nameKey,
        measure: measures[role],
        distanceType: distanceTypeFor(set),
        cardioMetrics,
        readOnly: props.readOnly,
        onDuration: (value: SecondsValue | null) => updateRole(role, {duration: value}),
        onDistance: (value: string | null) => updateRole(role, {distance: value}),
        onMeasure: (measure: Measure, distanceType?: DistanceType) =>
            setRoleMeasure(role, measure, distanceType),
        onPatch: (patch: Partial<SetOutput>) => updateRole(role, patch),
    });

    return (
        <workouts.ExerciseContext.Provider value={{exercise}}>
            <div className={classNames(CARD, "p-4")}>
                {warmup != null ? (
                    <Segment
                        role="Warmup"
                        {...segmentProps("WARM_UP", warmup, `${exercise.uuid}-warmup`)}
                        trailing={
                            <RemoveButton
                                label="Remove warmup"
                                readOnly={props.readOnly}
                                onClick={() => removeBoundary(warmup.uuid)}
                            />
                        }
                    />
                ) : (
                    <AddRow
                        label="Add warmup"
                        readOnly={props.readOnly}
                        onClick={() => addBoundary("WARM_UP")}
                    />
                )}

                <SegmentDivider />

                <Segment
                    role="On"
                    tone="work"
                    {...segmentProps("WORKING", work, `${exercise.uuid}-on`)}
                    trailing={
                        <Stepper
                            value={pendingRounds}
                            readOnly={props.readOnly}
                            onChange={(next) => setPendingRounds(Math.max(1, next))}
                        />
                    }
                />

                <SegmentDivider />

                <Segment
                    role="Off"
                    tone="rest"
                    {...segmentProps("REST", rest, `${exercise.uuid}-off`)}
                />

                <SegmentDivider />

                {cooldown != null ? (
                    <Segment
                        role="Cooldown"
                        {...segmentProps(
                            "COOL_DOWN",
                            cooldown,
                            `${exercise.uuid}-cooldown`,
                        )}
                        trailing={
                            <RemoveButton
                                label="Remove cooldown"
                                readOnly={props.readOnly}
                                onClick={() => removeBoundary(cooldown.uuid)}
                            />
                        }
                    />
                ) : (
                    <AddRow
                        label="Add cooldown"
                        readOnly={props.readOnly}
                        onClick={() => addBoundary("COOL_DOWN")}
                    />
                )}
            </div>
        </workouts.ExerciseContext.Provider>
    );
}

// The single cardio entry point: a steady reading or the interval block,
// depending on the exercise's structure. Callers (Workout.tsx) match cardio vs
// strength once and hand the narrowed cardio exercise here.
export function Cardio(props: {
    exercise: workouts.CardioExercise;
    readOnly: boolean;
    isTemplate: boolean;
    onSetsRemoved: (uuids: string[]) => void;
}) {
    return match(props.exercise)
        .with({structure: "INTERVALS"}, (exercise) => (
            <CardioIntervals
                exercise={exercise}
                readOnly={props.readOnly}
                isTemplate={props.isTemplate}
                onSetsRemoved={props.onSetsRemoved}
            />
        ))
        .with({structure: "STRAIGHT"}, (exercise) => (
            <SteadyCardioSets exercise={exercise} readOnly={props.readOnly} />
        ))
        .exhaustive();
}
